import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounce } from 'react-use'

import {
  formatCardNumber,
  formatExpiry,
  getCardNumberError,
  getCardTypeByValue,
  getCVCError,
  getExpiryDateError,
  PaymentCardType,
} from '@makyoapp/makyo-common'
import * as Icon from '@makyoapp/makyo-icon/icon'
import { CreditCardIcon, Field, Label, SimpleInput } from '@makyoapp/makyo-ui'

import { DEFAULT_DEBOUNCE_MS } from '@/shared/constants'

import { AddressFieldProps } from './AddressForm'

const errorMessageMap = {
  card: {
    cardNumberIsInvalid: 'Invalid card number, please double check',
    emptyCardNumber: "Card number can't be empty",
  },
  expiry: {
    expiryDateIsInvalid: 'Invalid expiry date, please double check',
    emptyExpiryDate: "Expiry date can't be empty",
    monthOutOfRange: "Month can't be out of range",
    yearOutOfRange: "Year can't be out of range",
    dateOutOfRange: "Date can't be out of range",
  },
  cvc: {
    cvcIsInvalid: 'Invalid CVC, please double check',
    emptyCVC: "CVC can't be empty",
  },
}

export function CardField({
  id,
  state,
  setState,
  cardType,
  setCardType,
}: AddressFieldProps & {
  cardType?: PaymentCardType
  setCardType: (cardType: PaymentCardType) => void
}) {
  const { t } = useTranslation()
  const [isInitial, setIsInitial] = useState<boolean>(true)
  const card = state.value.substring(0, 19 + 3)

  const [, cancelCard] = useDebounce(
    () => {
      if (!isInitial) {
        const ec = getCardNumberError(card)
        if (ec) {
          setState({
            value: card,
            disableButton: true,
            error: t(errorMessageMap.card[ec]),
          })
        } else {
          setState({
            value: card,
            disableButton: false,
            error: null,
          })
        }
      }
    },
    DEFAULT_DEBOUNCE_MS,
    [isInitial, card]
  )
  const cardIcon = cardType ? <CreditCardIcon variant={cardType.type} props={{ width: 24, height: 'auto' }} /> : null

  return (
    <Field>
      <Label required>{t('Card Number')}</Label>
      <SimpleInput
        id={id}
        required
        value={card}
        error={state.error}
        onChange={(e) => {
          setIsInitial(false)
          const value = e.currentTarget.value
          const cardNumber = value.replace(/\s/g, '')
          const ec = getCardNumberError(cardNumber)
          const cardType = getCardTypeByValue(cardNumber)
          setCardType(cardType)
          const formattedValue = formatCardNumber(cardNumber)

          if (ec) {
            setState({
              ...state,
              value: formattedValue,
              disableButton: true,
            })
          } else {
            setState({
              value: formattedValue,
              disableButton: false,
              error: null,
            })
            cancelCard()
          }
        }}
        type='text'
        autoComplete='cc-number'
        autoCorrect='off'
        spellCheck={false}
        placeholder={t('Card Number')}
        before={<div className='w-[24px]'>{cardIcon ? <div>{cardIcon}</div> : <div>{<Icon.CardAdd24 />}</div>}</div>}
      />
    </Field>
  )
}

export function ExpiryField({ id, state, setState }: AddressFieldProps) {
  const { t } = useTranslation()
  const [isInitial, setIsInitial] = useState<boolean>(true)
  const value = state.value
  const [, cancelExpiry] = useDebounce(
    () => {
      if (!isInitial) {
        const ec = getExpiryDateError(value)
        if (ec) {
          setState({
            value,
            disableButton: true,
            error: t(errorMessageMap.expiry[ec]),
          })
        } else {
          setState({
            value,
            disableButton: false,
            error: null,
          })
        }
      }
    },
    DEFAULT_DEBOUNCE_MS,
    [isInitial, value]
  )
  return (
    <Field>
      <Label required>{t('Expiry')}</Label>
      <SimpleInput
        id={id}
        required
        placeholder='MM/YY'
        value={value}
        error={state.error}
        autoComplete='cc-exp'
        type='text'
        data-form-type='date,expiration,credit_card,payment'
        onChange={(e) => {
          //const value = e.currentTarget.value
          const formatted = formatExpiry(e)
          if (formatted) {
            setIsInitial(false)
            const ec = getExpiryDateError(formatted)
            if (ec) {
              setState({
                ...state,
                value: formatted,
                disableButton: true,
              })
            } else {
              setState({
                value: formatted,
                disableButton: false,
                error: null,
              })
              cancelExpiry()
            }
          }
        }}
      />
    </Field>
  )
}

export function CVCField({ id, state, setState, cardType }: AddressFieldProps & { cardType?: PaymentCardType }) {
  const [isInitial, setIsInitial] = useState<boolean>(true)
  const { t } = useTranslation()
  const [, cancelCVC] = useDebounce(
    () => {
      if (!isInitial) {
        const ec = getCVCError(state.value, cardType)
        if (!!ec) {
          setState({
            ...state,
            disableButton: true,
            error: t(errorMessageMap.cvc[ec]),
          })
        } else {
          setState({
            ...state,
            error: null,
            disableButton: false,
          })
        }
      }
    },
    DEFAULT_DEBOUNCE_MS,
    [isInitial, state]
  )

  return (
    <Field>
      <Label required>CVC</Label>
      <SimpleInput
        id={id}
        required
        value={state.value}
        error={state.error}
        autoComplete='cc-csc'
        autoCorrect='off'
        spellCheck={false}
        data-form-type='payment,credit_card,cvv'
        type='text'
        maxLength={cardType?.type === 'Amex' ? 4 : 3}
        onChange={(e) => {
          setIsInitial(false)
          const value = e.currentTarget.value.replace(/\D/g, '')
          const ec = getCVCError(value, cardType)
          if (ec) {
            setState({
              ...state,
              disableButton: true,
              value,
            })
          } else {
            setState({
              disableButton: false,
              error: null,
              value,
            })
            cancelCVC()
          }
        }}
      />
    </Field>
  )
}
